import { PersistedSession } from './types';

const DB_NAME = 'upload-gateway';
const DB_VERSION = 1;
const STORE_NAME = 'sessions';

/**
 * Resume-after-refresh works by matching a re-selected/re-dropped file
 * against a persisted session using this key (project + relative path +
 * size + lastModified). A hard refresh drops the in-memory File handle —
 * the browser platform has no way around that outside the (Chromium-only,
 * permission-gated) File System Access API — so the user must re-add the
 * same file, and we detect the match and skip already-uploaded chunks
 * instead of starting over.
 */
export function computeFileKey(projectName: string, relativePath: string, file: File): string {
  return `${projectName}::${relativePath}::${file.size}::${file.lastModified}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'fileKey' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function runTransaction<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    const request = fn(store);
    request.onsuccess = () => resolve(request.result as T);
    request.onerror = () => reject(request.error);
  });
}

export async function saveSession(record: PersistedSession): Promise<void> {
  const db = await openDb();
  await runTransaction(db, 'readwrite', (store) => store.put(record));
}

export async function getSession(fileKey: string): Promise<PersistedSession | undefined> {
  const db = await openDb();
  return runTransaction<PersistedSession | undefined>(db, 'readonly', (store) => store.get(fileKey));
}

export async function deleteSession(fileKey: string): Promise<void> {
  const db = await openDb();
  await runTransaction(db, 'readwrite', (store) => store.delete(fileKey));
}

export async function listSessions(): Promise<PersistedSession[]> {
  const db = await openDb();
  return runTransaction<PersistedSession[]>(db, 'readonly', (store) => store.getAll());
}
